import {
	BaseRequestConfig,
	PlantStatusByContainmentResponse,
	HoldSystemsResponse,
	SearchResponse,
	AddSerialsResponse,
	SubmitContainmentPayload,
	AddContainmentResponse,
	MccApi,
} from "lib/api";
import { Plant } from "models";
import { PlantDTO } from "models/dto";
import * as constants from "lib/api/constants";
import { ApiPromiseClient } from "../apiClient/ApiClient";
import * as plantMapper from "models/mapper/plant";

import { PlantType } from "store";
import * as dotEnv from "lib/dotEnv";

export type PlantQuery =
	| { sectorTypeID: string; config?: BaseRequestConfig }
	| { sectorID: string; config?: BaseRequestConfig }
	| { typeID: string; config?: BaseRequestConfig }
	| { typeName: string; config?: BaseRequestConfig }
	| { config?: BaseRequestConfig };

export async function PlantStatusByContainment(
	api: ApiPromiseClient,
	id: string,
	config?: BaseRequestConfig
): Promise<PlantStatusByContainmentResponse<Plant>> {
	const raw = await api.Get<HoldSystemsResponse>(
		constants.Paths().plantStatusByContainment(id),
		{
			...config,
			// needs a longer timeout
			signal: MccApi.TimeoutAbortSignal(
				parseInt(dotEnv.Get("API_DOWNLOAD_TIMEOUT_MS"))
			),
		}
	);

	// Map each hold system separately
	const mapper = plantMapper.New();
	const holdSystems = (raw.holdSystems || []).map((hs) => ({
		id: hs.id,
		name: hs.name,
		containmentCount: hs.containmentCount || 0,
		totalSerialsUploaded: hs.totalSerialsUploaded || 0,
		plants: mapper.MapArray((hs.plants || []) as any),
	}));

	// Calculate overall totals across all hold systems
	let containmentSerialCount = 0;
	let totalSerialsUploaded = 0;
	const allPlants: Plant[] = [];

	for (const hs of holdSystems) {
		totalSerialsUploaded += hs.totalSerialsUploaded;
		containmentSerialCount += hs.containmentCount;
		for (const plant of hs.plants) {
			allPlants.push(plant);
		}
	}

	return {
		containmentSerialCount,
		totalSerialsUploaded,
		plants: allPlants, // For backward compatibility
		holdSystems, // New: separate data per hold system
	};
}
export async function PlantTypes(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<PlantType[]> {
	return api.Get<PlantType[]>(constants.Paths().plantTypes, config);
}

export async function Plants(
	api: ApiPromiseClient,
	query: PlantQuery = {}
): Promise<Plant[]> {
	let plantDTOs: PlantDTO[] = [];

	if ("sectorTypeID" in query) {
		plantDTOs = await api.Get(
			constants.Paths().plantsBySectorType(query.sectorTypeID),
			query.config
		);
	} else if ("sectorID" in query) {
		plantDTOs = await api.Get(
			constants.Paths().plantsBySector(query.sectorID),
			query.config
		);
	} else if ("typeID" in query) {
		plantDTOs = await api.Get(
			constants.Paths().plantsByPlantTypeId(query.typeID),
			query.config
		);
	} else if ("typeName" in query) {
		const types = await PlantTypes(api, query.config);
		const match = types.Find((t) => t.name === query.typeName);
		plantDTOs = await match
			.Map((t) =>
				api.Get<PlantDTO[]>(
					constants.Paths().plantsByPlantTypeId(t.id),
					query.config
				)
			)
			.UnwrapOrElse(() => Promise.resolve([]));
	} else {
		const types = await PlantTypes(api, query.config);
		const requests = types.Map((type) =>
			api.Get<PlantDTO[]>(
				constants.Paths().plantsByPlantTypeId(type.id),
				query.config
			)
		);
		const results = await Promise.all(requests);
		plantDTOs = make(results).flat();
	}

	return plantMapper
		.New()
		.MapArray(plantDTOs)
		.Sort((a, b) => a.name.localeCompare(b.name));
}
